import { captureMessage } from "@sentry/react";

import type { Middleware } from "@reduxjs/toolkit";
import type { FetchBaseQueryMeta } from "@reduxjs/toolkit/query";

import type { ErrorState } from "@state/errorSlice";
import type { RootState } from "@state/store";

type RejectedThunkAction = {
  type: string;
  meta: Record<string, unknown>;
  payload: {
    error?: ErrorState;
  };
};

type RTKQueryErrorAction = {
  type: string;
  meta: {
    baseQueryMeta?: FetchBaseQueryMeta;
  };
  payload: {
    data?: ErrorState;
    originalStatus?: number;
    status?: number;
  };
  error?: {
    message?: string;
  };
};

/**
 * Checks if the action is an RTK Query error action.
 */
const isRTKQueryErrorAction = (action: unknown): action is RTKQueryErrorAction =>
  typeof (action as RTKQueryErrorAction).type === "string" &&
  ["apiSlice/executeQuery/rejected", "apiSlice/executeMutation/rejected"].includes(
    (action as RTKQueryErrorAction).type,
  );

/**
 * Checks if the action is a rejected thunk error (e.g., app initialization errors).
 */
const isAppInitializationThunkErrorAction = (action: unknown): action is RejectedThunkAction =>
  typeof (action as RejectedThunkAction).type === "string" &&
  ((action as RejectedThunkAction).type === "monday/fetchMondayContext/rejected" ||
    (action as RejectedThunkAction).type === "monday/fetchInit/rejected");

/**
 * Middleware to handle and log API and Redux thunk errors.
 */
const errorMiddleware: Middleware<unknown, RootState> = (store) => (next) => (action: unknown) => {
  if (isRTKQueryErrorAction(action)) {
    const { data } = action.payload;
    const errorMessage = data?.message || action.error?.message || "Unknown error";
    const status = data?.status || action.payload.originalStatus || action.payload.status || 500;
    const eventId = data?.eventId;

    const request = action.meta.baseQueryMeta?.request;

    if (request) {
      const endpoint = request.url.split("?")[0].substring(request.url.indexOf("/api")) || "unknown";

      const payload = {
        message: errorMessage,
        status,
        query: {
          method: request.method,
          url: request.url,
          endpoint,
        },
        data,
        eventId,
      };

      // Log the error to Sentry if it hasn't been already
      if (!payload.eventId) {
        const mondayAccountId = store.getState().monday.context?.account.id || "unknown";

        payload.eventId = captureMessage(`API Error: ${endpoint}`, {
          level: "error",
          fingerprint: [endpoint, mondayAccountId],
          extra: { ...payload },
        });
      }

      store.dispatch({ type: "errors/addApiError", payload });
    }
  }

  // Handle rejected thunk actions (e.g., app initialization errors)
  else if (isAppInitializationThunkErrorAction(action)) {
    const errorMessage = action.payload.error?.message || "Unknown error";
    const status = action.payload.error?.status || 500;
    const endpoint = action.type;

    const payload = {
      message: errorMessage,
      status,
      query: {
        method: "THUNK",
        endpoint,
      },
      eventId: "unknown",
    };

    const mondayAccountId = store.getState().monday.context?.account.id || "unknown";

    payload.eventId = captureMessage(`Thunk Error: ${endpoint}`, {
      level: "error",
      fingerprint: [endpoint, mondayAccountId],
      extra: { ...payload },
    });

    store.dispatch({ type: "errors/addApiError", payload });
  }

  return next(action);
};

export default errorMiddleware;
